import { useGetProjectTestSessionQuery, useGetProjectPullRequestQuery } from '../hooks/bug-management/useProjectFindings'
import { sessionBugData } from '../lib/bug-management/testRunFindings'
import DailyMonitoringSessions from '../components/DailyMonitoringSessions'
import SessionSummarizers from '../components/SessionSummarizers'
import AppArtifactsDownload from '../components/AppArtifactsDownload'
import { testSessionStatus } from '../lib/testSessionStatus'
import { useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Link, useNavigate } from '../components/nav'
import { AlertTriangle, ArrowLeft, Compass, ExternalLink, GitBranch, GitCommit, GitPullRequest, RefreshCw } from 'lucide-react'
import type { PullRequest, PullRequestDetail, TestRunNotice, TestSessionExploration } from '../data/projectTypes'
import type { RootState } from '../store'
import {
  projectDataApi,

  useGetProjectQuery,
  useGetTestRunNoticesQuery,
} from '../services/projectDataApi'
import { githubPrRunStatus, githubVersionIdFromTestRunId, isGithubPrNotFound } from '../lib/githubTestRun'
import { isProjectAccessDeniedError } from '../lib/projectQueryError'
import { useSignInOnUnauthorized } from '../hooks/useSignInOnUnauthorized'
import { previewDeploymentFailure, previewWait } from '../lib/previewDeployment'
import { projectPause, type ProjectPause } from '../lib/projectPause'
import Spinner from '../components/Spinner'
import OverviewExplorations from '../components/OverviewExplorations'
import TaskEventReplay from '../components/TaskEventReplay'
import { taskDetailUrl } from '../lib/taskDetailPath'
import StatusIndicator from '../components/StatusIndicator'
import EnvironmentBadge from '../components/EnvironmentBadge'
import MarkdownLite from '../components/MarkdownLite'
import JourneyGroupedList, { ReportedBugsList } from '../components/JourneyGroupedList'
import CommentsSection from '../components/CommentsSection'
import './TestRunSessionDetail.css'

function shortSha(sha: string): string {
  return sha.length > 12 ? sha.slice(0, 12) : sha
}

// What QA said about this round — the "testing started" summary of the change under test, and the
// "testing finished" summary of what it found. These used to be posted into the project chat, where a
// busy project's rounds buried the conversation; they belong to the run they describe, so they read here
// as context ABOVE the results. Ordered started-then-finished by the query (oldest first).
function RunNotice({ notice }: { notice: TestRunNotice }) {
  const [expanded, setExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = contentRef.current
    if (!element || expanded) return

    const measure = () => setHasOverflow(element.scrollHeight > element.clientHeight + 1)
    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [expanded, notice.content])

  const contentId = `run-detail-notice-content-${notice.id}`
  return (
    <article
      className={`run-detail__notice run-detail__notice--${notice.kind}`}
      data-testid={`run-detail-notice-${notice.kind}`}
    >
      <div
        ref={contentRef}
        id={contentId}
        className={`run-detail__notice-content${expanded ? ' run-detail__notice-content--expanded' : ' run-detail__notice-content--collapsed'}`}
      >
        <MarkdownLite text={notice.content} className="run-detail__notice-md" looseLists />
      </div>
      {hasOverflow && (
        <button
          type="button"
          className={`run-detail__notice-toggle${expanded ? ' run-detail__notice-toggle--expanded' : ''}`}
          aria-controls={contentId}
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          <span className="run-detail__notice-toggle-label">{expanded ? 'View less' : 'View more'}</span>
        </button>
      )}
    </article>
  )
}

// Reserved slot for a round summary that is certain to arrive but has not loaded yet: the same
// section structure the real one renders, with skeleton bars inside the same collapsed notice box,
// so the late response swaps in without displacing the already-rendered content below (issue #3882).
function RoundSummarySkeleton({ kind, title }: { kind: 'findings' | 'coverage'; title: string }) {
  return (
    <section className="run-detail__section" data-testid={`run-detail-${kind}-summary-loading`} aria-busy="true">
      <h2 className="run-detail__section-title">{title}</h2>
      <div className="run-detail__notices">
        <article className={`run-detail__notice run-detail__notice--${kind}`}>
          <div className="run-detail__notice-content run-detail__notice-content--collapsed">
            <div className="run-detail__notice-md">
              {[94, 100, 88, 96, 82, 92, 70, 45].map((width, index) => (
                <span key={index} className="run-detail__skeleton-line" style={{ width: `${width}%` }} />
              ))}
            </div>
          </div>
        </article>
      </div>
    </section>
  )
}

function RunNotices({ notices, explorations = [], pendingRoundSummaries = false }: {
  notices: TestRunNotice[]
  explorations?: TestSessionExploration[]
  pendingRoundSummaries?: boolean
}) {
  const labels: Record<Exclude<TestRunNotice['kind'], 'obstacles'>, string> = {
    started: 'Initial description',
    findings: 'Findings summary',
    coverage: 'Coverage summary',
    finished: 'Run summary',
  }
  // Journey obstacle notices summarize child execution, not why exploration stopped.
  const failureDetail = previewDeploymentFailure(notices)
  const runNotices = notices.filter(notice => notice.kind !== 'obstacles' &&
    !(failureDetail && notice.kind === 'finished' && notice.source?.startsWith('preview-never-arrived:')))
  const blockedExplorations = explorations.filter(exploration => exploration.status === 'blocked')
  if (!failureDetail && runNotices.length === 0 && blockedExplorations.length === 0) {
    // A completed round's findings/coverage summaries are written alongside its completed status, so
    // they are certain to arrive; hold their slots through the first notices load instead of letting
    // the response insert two sections above the journey list after render.
    if (pendingRoundSummaries) {
      return (
        <>
          <RoundSummarySkeleton kind="findings" title={labels.findings} />
          <RoundSummarySkeleton kind="coverage" title={labels.coverage} />
        </>
      )
    }
    return null
  }
  return (
    <>
      {failureDetail && (
        <section className="run-detail__awaiting run-detail__awaiting--stalled" role="alert" data-testid="run-detail-deployment-failed">
          <AlertTriangle size={18} aria-hidden />
          <div className="run-detail__awaiting-body">
            <h2 className="run-detail__awaiting-title">Testing did not start: no preview deployment detected</h2>
            <MarkdownLite text={failureDetail} className="run-detail__notice-md" looseLists />
          </div>
        </section>
      )}
      {blockedExplorations.map(exploration => (
        <section className="run-detail__section" data-testid="run-detail-exploration-blocked" key={exploration.id}>
          <h2 className="run-detail__section-title">Exploration blocked</h2>
          <div className="run-detail__notices">
            <article className="run-detail__notice run-detail__notice--obstacles">
              <p>{exploration.blocked_detail?.trim() || 'No specific reason was recorded for this blocked exploration.'}</p>
            </article>
          </div>
        </section>
      ))}
      {runNotices.map((notice) => notice.kind !== 'obstacles' && (
        <section className="run-detail__section" data-testid={`run-detail-${notice.kind}-summary`} key={notice.id}>
          <h2 className="run-detail__section-title">{labels[notice.kind]}</h2>
          <div className="run-detail__notices"><RunNotice notice={notice} /></div>
        </section>
      ))}
    </>
  )
}

function LinkedBugRetests({ pr }: { pr: PullRequestDetail }) {
  if (!pr.bug_verifications.length) return null
  return <section className="run-detail__section">
    <h2 className="run-detail__section-title">Linked bug retests</h2>
    {pr.bug_verifications.map(result => <p key={result.bug_id}>
      <Link to={`/projects/${result.project_id}/bugs/${result.bug_id}`}>{result.title}</Link>
      {' — '}{result.detail}
      {result.test_run_id && <> · <Link to={taskDetailUrl(result.project_id, result.test_run_id)}>View run</Link></>}
    </p>)}
    <p>Preview results leave bugs open. Linked bugs are marked fixed when the PR merges into the project’s main branch.</p>
  </section>
}

function GithubSourceCard({ pr, pause }: { pr: PullRequest; pause: ProjectPause | null }) {
  return (
    <div className="run-detail__github-card" data-testid="run-detail-github-source">
      <div className="run-detail__github-main">
        <a href={pr.pr_url} target="_blank" rel="noreferrer" data-analytics-event="test-run-github-pr-open">
          <GitPullRequest size={14} aria-hidden /> PR #{pr.pr_number}
        </a>
        <StatusIndicator status={githubPrRunStatus(pr)} pause={pause} withIcon />
      </div>
      <div className="run-detail__github-meta">
        <span><GitBranch size={12} aria-hidden /> {pr.branch_name}</span>
        <span><GitCommit size={12} aria-hidden /> {shortSha(pr.head_sha)}</span>
        <span>{pr.journeys_count} {pr.journeys_count === 1 ? 'journey' : 'journeys'}</span>
        <span>{pr.bug_count} {pr.bug_count === 1 ? 'bug' : 'bugs'}</span>
        {pr.deployed_url && (
          <a href={pr.deployed_url} target="_blank" rel="noreferrer" data-analytics-event="test-run-github-deployment-open">
            <ExternalLink size={12} aria-hidden /> Preview
          </a>
        )}
      </div>
    </div>
  )
}

// A version round's browse pass is the VERSION-ADDED task, which — unlike the initial pass — creates no
// exploration row: it reuses the same browse machinery but authors journeys and files bugs straight
// against the version (see chat/version-added-run-script.ts). Its recording is still the exploration of
// this round, so render it as one, sourced by task rather than by exploration id. Without this a round
// that authored no journeys and filed no bugs reads as "no exploration ran" — indistinguishable from QA
// never having started, even though a full browse happened and is replayable.
//
// The task is addressed per-VERSION (session.version_task_id, or the PR's own task on the pre-session
// view where there is no session to read it from). A PR accumulates one version — and so one of these —
// per pushed commit, and each gets its own Test Run page.
function VersionBrowseReplay({ projectId, taskId, live }: {
  projectId: string
  taskId: string | null
  live: boolean
}) {
  if (!taskId) return <p className="run-detail__empty">No exploration ran in this test run.</p>

  return (
    <div className="ov-explore" data-testid="run-detail-version-browse">
      <div className="ov-explore__head">
        <span className="ov-explore__title">
          <Compass size={18} strokeWidth={1.75} aria-hidden /> Exploration
        </span>
      </div>
      <div className="ov-explore__stage">
        <TaskEventReplay
          // Keyed so moving between two test runs (or onto a retry's replacement task) mounts a fresh
          // player rather than handing the live one a second recording to append.
          key={taskId}
          source={{ taskId }}
          taskHref={taskDetailUrl(projectId, taskId)}
          active
          live={live}
        />
      </div>
    </div>
  )
}

// The headline fact for a revision QA is still holding: nothing has been tested, and nothing will be
// until this commit's own preview deployment URL is found. It leads the page because every other thing
// on it (the empty run, the absent journeys, the missing bugs) is a consequence of this one — read
// without it, an untested revision looks like a clean one.
function AwaitingDeploymentBanner({ pr }: { pr: PullRequest }) {
  const wait = previewWait(pr)
  if (!wait) return null
  const waited = wait.minutes === null ? null : `${wait.minutes} min`
  return (
    <section
      className={`run-detail__awaiting${wait.stalled ? ' run-detail__awaiting--stalled' : ''}`}
      role="status"
      data-testid="run-detail-awaiting-deployment"
    >
      <span className="run-detail__awaiting-icon" aria-hidden>
        {wait.stalled ? <AlertTriangle size={18} /> : <Spinner size={16} />}
      </span>
      <div className="run-detail__awaiting-body">
        <h2 className="run-detail__awaiting-title">No deployment detected yet</h2>
        <p className="run-detail__awaiting-text">
          {wait.stalled
            // Past the point where a preview normally would exist, so stop implying it is on its way and
            // name the likely cause instead — the same read the GitHub tab and the chat agent give.
            ? <>
                QA has been waiting {waited ?? 'a while'} for a preview deployment of {shortSha(pr.head_sha)} and
                none has appeared, so the preview build most likely never published one. Nothing has been tested
                for this commit; QA starts automatically if a preview URL shows up.
              </>
            : <>
                QA is waiting for this commit's preview deployment{waited ? ` (${waited} so far)` : ''}. Testing
                has not started and will begin automatically once a preview URL for {shortSha(pr.head_sha)} is
                published.
              </>}
        </p>
      </div>
    </section>
  )
}

// The pre-session view: QA has a version for this PR revision but hasn't produced any artifact yet, so
// no test session exists. The 'started' notice is stored against the VERSION precisely so this view can
// show what is being tested during that window.
function GithubOnlyTestRunDetail({ projectId, pr, notices, pause }: {
  projectId: string
  pr: PullRequestDetail
  notices: TestRunNotice[]
  pause: ProjectPause | null
}) {
  const navigate = useNavigate()
  // While the deployment is still missing the version task exists but has never run, so its replay is an
  // empty frame — showing it reads as "exploration happened and found nothing". The banner is the whole
  // story of this page until a preview URL lands.
  const awaitingDeployment = previewWait(pr) !== null
  return (
    <div className="run-detail" data-testid="test-run-detail">
      <div className="run-detail__head">
        <button
          type="button"
          className="run-detail__back"
          onClick={() => navigate(`/projects/${projectId}/test-runs`)}
          data-testid="run-detail-back"
          data-analytics-event="test-run-detail-back"
        >
          <ArrowLeft size={15} aria-hidden />
          Test Runs
        </button>
        <div className="run-detail__title-row">
          <h1 className="run-detail__title">
            {pr.pr_title ? `PR #${pr.pr_number}: ${pr.pr_title}` : `PR #${pr.pr_number}`}
          </h1>
          <StatusIndicator status={githubPrRunStatus(pr)} pause={pause} withIcon />
          <EnvironmentBadge artifact={{ environment_name: 'Preview', environment_kind: 'preview', pr_number: pr.pr_number, pr_repo_full_name: pr.repo_full_name }} />
        </div>
        <p className="run-detail__meta">
          GitHub pull request · {pr.branch_name} · {shortSha(pr.head_sha)}
        </p>
      </div>

      <AwaitingDeploymentBanner pr={pr} />

      <RunNotices notices={notices} />
      {pr && <LinkedBugRetests pr={pr} />}

      {!awaitingDeployment && (
        <section className="run-detail__section" data-testid="run-detail-explorations">
          <VersionBrowseReplay
            projectId={projectId}
            taskId={pr.task_id}
            live={!pause && (pr.version_status === 'in-progress' || pr.version_status === 'pending')}
          />
        </section>
      )}

      <section className="run-detail__section" data-testid="run-detail-github">
        <h2 className="run-detail__section-title">GitHub</h2>
        <GithubSourceCard pr={pr} pause={pause} />
      </section>
    </div>
  )
}

// One test run (test session): the exploration(s) that mapped the app — embedded as the replay
// preview, scoped to this run — plus the journeys it ran and the bugs it filed.
export default function TestRunSessionDetail() {
  const { projectId, sessionId } = useParams<{ projectId: string; sessionId: string }>()
  const navigate = useNavigate()
  const user = useSelector((state: RootState) => state.auth.user)
  const projectQuery = useGetProjectQuery(projectId ?? '', { skip: !projectId })

  // Resolve GitHub links directly by version; never download the project's entire run history.
  const githubVersionId = sessionId ? githubVersionIdFromTestRunId(sessionId) : null
  const sessionsQuery = useGetProjectTestSessionQuery({
    projectId: projectId ?? '',
    ...(githubVersionId ? { versionId: githubVersionId } : { sessionId: sessionId ?? '' }),
  }, {
    skip: !projectId || !sessionId,
    pollingInterval: 10_000,
    skipPollingIfUnfocused: true,
  })
  const session = sessionsQuery.currentData?.session
  const loading = sessionsQuery.isLoading || (sessionsQuery.isFetching && !sessionsQuery.currentData)

  // This page is the EXPANDED view of one test run, so it asks for just that run's pull request rather
  // than the project's whole PR history.
  const isMonitoring = Boolean(session?.monitoring_day)
  const prVersionId = isMonitoring ? null : session?.version_id ?? githubVersionId
  const prQueryArgs = { projectId: projectId ?? '', versionId: prVersionId ?? '' }
  // Read the settled lookup state from the store rather than off the hook below: the poll option must
  // already be 0 on the render that first observes the 404, or one more doomed request goes out
  // before the interval is cleared.
  const githubPrState = useSelector(projectDataApi.endpoints.getProjectPullRequest.select(prQueryArgs))
  const githubPrMissing = isGithubPrNotFound(githubPrState.error)
  const githubPrQuery = useGetProjectPullRequestQuery(prQueryArgs, {
    skip: !projectId || !prVersionId,
    skipPollingIfUnfocused: true,
    // 'Pull request not found' is final for a version with no GitHub PR, so the 10s poll would only
    // re-send a request that can never succeed. Stop it — reloading the page or opening the run
    // again runs the lookup fresh, but nothing on a timer can change its answer.
    pollingInterval: githubPrMissing ? 0 : 10_000,
  })
  const githubPr = githubPrQuery.data?.pr

  // Both keys, because a round's two notices are stored against different ones: 'started' against the
  // version (written before any session existed) and 'finished' against the session. Passing only the
  // one we happen to have addressed the run by would show half the round. Same version as the PR fetch
  // above resolves to.
  const noticesQuery = useGetTestRunNoticesQuery(
    { projectId: projectId ?? '', sessionId: session?.id ?? null, versionId: prVersionId },
    {
      skip: isMonitoring || !projectId || (!session && !prVersionId),
      // Keep the final poll alive long enough to observe the finished notice that is written alongside
      // the session's completed status; the query stops naturally when the detail page unmounts.
      pollingInterval: 10_000,
      skipPollingIfUnfocused: true,
    },
  )
  const notices = noticesQuery.data?.notices ?? []

  // Either project-scoped request can establish that this private run needs authentication. In
  // particular, do not depend only on the project query: RTK Query may satisfy it from a prefetched
  // cache while the detail's session request receives the authoritative 401.
  const redirectingToLogin = useSignInOnUnauthorized(projectQuery.error ?? sessionsQuery.error)

  // A paused project has no container running anything, but the run row and its journey runs keep the
  // 'in-progress' status they were frozen at — so every badge on this page reads through the pause.
  const pause = projectPause(projectQuery.data)

  // A failed fetch on any of this view's requests is how an alive run impersonates a deleted one: with
  // every query still "done but empty-handed", the !session branch below falls through to "That test
  // run no longer exists" — a false claim about the run (the reported session had both data requests
  // 502 on page load). Read before the not-found fallback so a broken fetch gets the honest card.
  const runDataFailed = sessionsQuery.isError || projectQuery.isError || githubPrQuery.isError

  // Refetch the sessions and project queries; the PR and notices queries stay skipped until a session
  // or a `github-pr-` version id resolves, so gate their refetch on the same condition the hooks use.
  const retryRunData = () => {
    void sessionsQuery.refetch()
    void projectQuery.refetch()
    if (prVersionId) {
      void githubPrQuery.refetch()
      void noticesQuery.refetch()
    }
  }

  // Every hook above this line: the Rules of Hooks forbid reaching a return before one of them.
  if (redirectingToLogin) {
    return <div className="run-detail__loading" role="status" data-testid="run-detail-loading">Loading test run…</div>
  }

  if (!projectId || !sessionId) {
    return <div className="run-detail__error" role="alert">Test run not found.</div>
  }

  if (sessionsQuery.isError) {
    // A deterministic 403 "Access denied" never clears on refetch (see isProjectAccessDeniedError):
    // the recorded failure was this page offering Retry for one, sixteen polls in a row, each
    // re-firing the identical request for the identical denial. That state explains the denial and
    // offers the way out instead. Every other failure can succeed on a later attempt and keeps a
    // Retry, which now refetches the run's every query like the fetch-error state below.
    if (isProjectAccessDeniedError(sessionsQuery.error)) {
      return <div className="run-detail__error" role="alert" data-testid="run-detail-access-denied">
        <p>You do not have access to this project.</p>
        <Link to={`/projects/${projectId}/test-runs`}>Back to test runs</Link>
      </div>
    }
    return <div className="run-detail__error" role="alert" data-testid="run-detail-load-error">
      <p>Could not load this test run and its journeys.</p>
      <button type="button" onClick={retryRunData}>Retry</button>
    </div>
  }

  if (!session) {
    // The PR fetch counts as loading here: with no session, a `github-pr-…` link has nothing else to
    // render from, and reporting "no longer exists" before it lands would be wrong.
    if (projectQuery.isLoading || loading || githubPrQuery.isLoading) {
      return <div className="run-detail__loading" role="status" data-testid="run-detail-loading">Loading test run…</div>
    }
    if (githubPr) return <GithubOnlyTestRunDetail projectId={projectId} pr={githubPr} notices={notices} pause={pause} />

    // The fetches failed, so the run's absence is NOT established — the not-found fallback below would
    // falsely claim the run is gone. Report the failure with a way out instead.
    if (runDataFailed) {
      return (
        <div className="run-detail__fetch-error" role="alert" data-testid="run-detail-fetch-error">
          <p className="run-detail__fetch-error-message">
            We couldn’t load this test run — the request failed, so its results are unavailable right now.
          </p>
          <div className="run-detail__fetch-error-actions">
            <button
              type="button"
              className="run-detail__fetch-error-retry"
              onClick={retryRunData}
              data-testid="run-detail-retry"
              data-analytics-event="test-run-detail-retry"
            >
              <RefreshCw size={14} aria-hidden />
              Retry
            </button>
            <Link to={`/projects/${projectId}/test-runs`}>Back to test runs</Link>
          </div>
        </div>
      )
    }

    return (
      <div className="run-detail__error" role="alert" data-testid="run-detail-not-found">
        <p>That test run no longer exists.</p>
        <Link to={`/projects/${projectId}/test-runs`}>Back to test runs</Link>
      </div>
    )
  }

  const started = new Date(session.started_at)
  const { findings, bug_associations } = sessionBugData(session)
  const bugCount = findings.new_bugs.length
  const rediscoveryCount = findings.rediscoveries.length
  const journeyRunIds = new Set(session.journeyRuns.flatMap(run => run.attempt_ids))
  const explorationBugs = bug_associations.filter(bug => !bug.test_run_id || !journeyRunIds.has(bug.test_run_id))
  const hasExplorationRecord = session.explorations.length > 0

  // A completed round always carries the findings/coverage summaries, so its first notices load
  // reserves those two slots. Background refetches keep the cached data, so this holds only before
  // the first response — polling never re-triggers the skeletons.
  const reserveRoundSummaries = session.status === 'completed' && !noticesQuery.data && noticesQuery.isFetching

  return (
    <div className="run-detail" data-testid="test-run-detail">
      <div className="run-detail__head">
        <button
          type="button"
          className="run-detail__back"
          onClick={() => navigate(`/projects/${projectId}/test-runs`)}
          data-testid="run-detail-back"
          data-analytics-event="test-run-detail-back"
        >
          <ArrowLeft size={15} aria-hidden />
          Test Runs
        </button>
        <div className="run-detail__title-row">
          <h1 className="run-detail__title">{session.title}</h1>
          <StatusIndicator status={testSessionStatus(session)} pause={pause} withIcon />
          {!isMonitoring && session.status === 'completed' && <AppArtifactsDownload projectId={projectId} />}
          <EnvironmentBadge artifact={{
            environment_name: session.environment_name,
            environment_kind: session.environment_kind,
            pr_number: githubPr?.pr_number ?? session.pr_number,
            pr_repo_full_name: githubPr?.repo_full_name ?? session.pr_repo_full_name,
          }} />
        </div>
        <p className="run-detail__meta">
          {isMonitoring ? <>Monitoring day {session.monitoring_day} · {session.monitoring_timezone}</> : <>Started {Number.isNaN(started.getTime()) ? '—' : started.toLocaleString()}</>}
          {' · '}
          {session.journeyRuns.length} {session.journeyRuns.length === 1 ? 'journey' : 'journeys'}
          {' · '}
          {bugCount} new {bugCount === 1 ? 'bug' : 'bugs'}
          {rediscoveryCount > 0 && <> · {rediscoveryCount} rediscoveries</>}
        </p>
      </div>

      {/* Context before results: what QA was told to test and what it concluded. */}
      {!isMonitoring && <RunNotices
        notices={notices}
        explorations={session.explorations}
        pendingRoundSummaries={reserveRoundSummaries}
      />}
      {session.monitoring_day && <section className="run-detail__section" aria-label="Daily monitoring report">
        <SessionSummarizers projectId={projectId} day={session.monitoring_day} />
        <p className="run-detail__empty">Reports use each summarizer’s configured timezone. Their coverage may differ from this run’s monitoring day.</p>
      </section>}
      {githubPr && <LinkedBugRetests pr={githubPr} />}

      {githubPr && (
        <section className="run-detail__section" data-testid="run-detail-github">
          <h2 className="run-detail__section-title">GitHub</h2>
          <GithubSourceCard pr={githubPr} pause={pause} />
        </section>
      )}

      {/* Exploration first: it is what discovered the journeys below. A persisted exploration record
          renders as the replay carousel; a version round's browse pass leaves no record, so it is
          sourced by task instead. */}
      {!isMonitoring && <section className="run-detail__section" data-testid="run-detail-explorations">
        {hasExplorationRecord ? (
          <OverviewExplorations
            projectId={projectId}
            explorationIds={session.explorations.map(exploration => exploration.id)}
          />
        ) : (
          <>
            {!session.version_task_id && <h2 className="run-detail__section-title">Exploration</h2>}
            <VersionBrowseReplay
              projectId={projectId}
              taskId={session.version_task_id}
              live={!pause && session.status === 'in-progress'}
            />
          </>
        )}
      </section>}

      {explorationBugs.length > 0 && (
        <section className="run-detail__exploration-bugs" data-testid="run-detail-exploration-bugs">
          <h3>{isMonitoring ? 'Bugs reported during monitoring' : 'Bugs Reported by Exploration'}</h3>
          <ReportedBugsList
            bugs={explorationBugs}
            projectId={projectId}
            label={isMonitoring ? 'Bugs reported during monitoring' : 'Bugs reported during exploration'}
            nested={false}
          />
        </section>
      )}

      <section className="run-detail__section" data-testid="run-detail-journeys">
        <h2 className="run-detail__section-title">Journeys</h2>
        {session.journeyRuns.length === 0 ? (
          <p className="run-detail__empty">No journeys ran in this test run.</p>
        ) : (
          /* `?run=` tells the journey page which test run the visitor came from, so its breadcrumb
             leads back to THIS run rather than to the journey's most recent one. */
          <JourneyGroupedList
            runs={session.journeyRuns}
            bugs={bug_associations}
            projectId={projectId}
            canReorder={!!user && projectQuery.data?.user_role !== 'viewer'}
            pause={pause}
            onOpen={journeyId => navigate(`/projects/${projectId}/journeys/${journeyId}?run=${encodeURIComponent(session.id)}`)}
          />
        )}
      </section>

      {isMonitoring && <DailyMonitoringSessions key={session.id} projectId={projectId} runId={session.id} />}

      <CommentsSection projectId={projectId} targetType="test_run" targetId={session.id} />
    </div>
  )
}
